import type { CmsManifest } from '@vastra/types';
import { initCmsI18n, reloadCmsI18n, startCmsI18nManifestPolling, t } from '@vastra/i18n';

type Portal = 'admin' | 'customer';

function env(name: string): string {
  // IMPORTANT: avoid `process` at runtime in the browser; webpack inlines REACT_APP_* when present.
  return typeof process !== 'undefined' && process.env && process.env[name] ? String(process.env[name]) : '';
}

function storeOrigin(): string {
  const raw = env('REACT_APP_STORE_URL').trim();
  if (!raw) return '';
  return raw.replace(/\/$/, '');
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export async function initI18n(portal: Portal = 'customer', locale = 'en'): Promise<void> {
  const origin = storeOrigin();
  return initCmsI18n(i18nOpts(origin), portal, locale);
}

export { t };

export async function reloadI18n(portal: Portal = 'customer', locale = 'en'): Promise<void> {
  const origin = storeOrigin();
  return reloadCmsI18n(i18nOpts(origin), portal, locale);
}

/** Start a background poll that auto-reloads i18n when the manifest changes on S3. */
export function startI18nPolling(
  portal: Portal = 'customer',
  locale = 'en',
  intervalMs = 60_000,
): () => void {
  const origin = storeOrigin();
  if (!origin) return () => {};
  return startCmsI18nManifestPolling(i18nOpts(origin), portal, locale, { intervalMs });
}

function i18nOpts(origin: string) {
  return {
    baseUrl: origin,
    fetchManifest: (b: string) => fetchJson<CmsManifest>(`${b}/productdata/data/cms/manifest.json`),
    fetchFileContent: (b: string, file: { key: string }) => fetchJson<Record<string, unknown>>(`${b}/productdata/${file.key}`),
  };
}

